import Axios from "axios";
import { MCP_CHAT_URL } from "../shared/mcp-chat-config";

export type RecorderState = "idle" | "recording" | "processing" | "error";

export interface RecorderCallbacks {
  onStateChange: (state: RecorderState) => void;
  onTimeUpdate: (seconds: number) => void;
  onTranscription: (text: string) => void;
  onError: (message: string) => void;
  onAudioLevel: (level: number) => void;
}

export class VoiceRecorder {
  private callbacks: RecorderCallbacks;
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private elapsedSeconds = 0;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private animFrameId: number | null = null;
  private state: RecorderState = "idle";
  private destroyed = false;

  constructor(callbacks: RecorderCallbacks) {
    this.callbacks = callbacks;
  }

  getState(): RecorderState {
    return this.state;
  }

  async startRecording(): Promise<void> {
    if (this.destroyed || this.state !== "idle") return;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });
    } catch (err: any) {
      const message =
        err.name === "NotAllowedError" || err.name === "PermissionDeniedError"
          ? "Microphone access denied. Please allow microphone permission."
          : err.name === "NotFoundError"
            ? "No microphone found. Please connect a microphone."
            : "Could not access microphone. Please try again.";
      this.setState("error");
      this.callbacks.onError(message);
      return;
    }

    // Set up audio analyser for level metering
    try {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);
      this.startLevelMetering();
    } catch {
      // Non-critical — continue without level metering
    }

    // Choose best available codec
    const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"].find((mt) =>
      MediaRecorder.isTypeSupported(mt)
    );

    this.chunks = [];
    this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      if (this.state === "recording") {
        this.processRecording();
      }
      // If cancelled, chunks are discarded in cancelRecording()
    };

    this.mediaRecorder.onerror = () => {
      this.setState("error");
      this.callbacks.onError("Recording failed. Please try again.");
      this.cleanup();
    };

    this.mediaRecorder.start(250); // Collect data every 250ms
    this.elapsedSeconds = 0;
    this.callbacks.onTimeUpdate(0);
    this.startTimer();
    this.setState("recording");
  }

  stopRecording(): void {
    if (this.state !== "recording" || !this.mediaRecorder) return;
    // State stays "recording" until onstop fires → processRecording()
    this.mediaRecorder.stop();
    this.stopTimer();
    this.stopLevelMetering();
  }

  cancelRecording(): void {
    if (this.state !== "recording" && this.state !== "processing") return;
    this.chunks = [];
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      // Temporarily override onstop to prevent processRecording
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.stop();
    }
    this.stopTimer();
    this.stopLevelMetering();
    this.releaseStream();
    this.setState("idle");
  }

  destroy(): void {
    this.destroyed = true;
    this.cancelRecording();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }

  // ── Private ──

  private setState(newState: RecorderState): void {
    this.state = newState;
    if (!this.destroyed) {
      this.callbacks.onStateChange(newState);
    }
  }

  private startTimer(): void {
    this.timerInterval = setInterval(() => {
      this.elapsedSeconds++;
      this.callbacks.onTimeUpdate(this.elapsedSeconds);
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private startLevelMetering(): void {
    if (!this.analyser) return;
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    const tick = () => {
      if (this.destroyed || !this.analyser) return;
      this.analyser.getByteFrequencyData(dataArray);

      // RMS-ish: average of frequency magnitudes, normalized to 0-1
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length / 255;
      // Apply a slight curve to make it more responsive
      const level = Math.min(1, avg * 2.5);
      this.callbacks.onAudioLevel(level);

      this.animFrameId = requestAnimationFrame(tick);
    };

    this.animFrameId = requestAnimationFrame(tick);
  }

  private stopLevelMetering(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.callbacks.onAudioLevel(0);
  }

  private releaseStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  private cleanup(): void {
    this.stopTimer();
    this.stopLevelMetering();
    this.releaseStream();
    this.chunks = [];
    this.mediaRecorder = null;
  }

  private async processRecording(): Promise<void> {
    this.setState("processing");
    this.releaseStream();

    const blob = new Blob(this.chunks, {
      type: this.mediaRecorder?.mimeType || "audio/webm"
    });
    this.chunks = [];
    this.mediaRecorder = null;

    if (blob.size === 0) {
      this.setState("error");
      this.callbacks.onError("No audio recorded. Please try again.");
      this.setState("idle");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");

      const response = await Axios.post<{ text?: string; error?: string }>(`${MCP_CHAT_URL}/speech-to-text`, formData);
      const data = response.data;

      if (!data?.text || data.text.trim().length === 0) {
        this.setState("error");
        this.callbacks.onError("No speech detected. Please try again.");
        this.setState("idle");
        return;
      }

      this.callbacks.onTranscription(data.text.trim());
      this.setState("idle");
    } catch (err: any) {
      this.setState("error");
      const isNetworkError = Axios.isAxiosError(err) && !err.response;
      const serverError = Axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : null;
      const message = isNetworkError
        ? "Cannot reach server. Please check your connection."
        : serverError || err.message || "Transcription failed. Please try again.";
      this.callbacks.onError(message);
      this.setState("idle");
    }
  }
}
